/**
 * **The week pattern while it is being edited** — booking-system R3's weekly grid, as the Grafik
 * tab's „Tydzień wzorcowy" card holds it until „Zapisz".
 *
 * `PUT /trainer-availability/weekly` REPLACES the whole grid: what is sent is the grid afterwards.
 * So the card edits a draft of all seven days and sends it whole; nothing is written window by
 * window. The server judges the draft by `WindowGridValidator` (train-with-me
 * `Modules/Booking/Services/WindowGridValidator.cs`) and `AvailabilityProcessor.ReplaceWeeklyAsync`
 * (at most seventy windows); {@link patternVerdict} reaches the same verdict first, so the rows
 * that fight are marked and „Zapisz" says why it will not send, instead of a 400 naming the whole
 * request. The two places the rules could drift are pinned by the tests beside this file: windows
 * that MEET end-to-start are fine, and a reversed window is a different refusal from an empty one.
 *
 * ── A day is on while it has windows ─────────────────────────────────────────────────────────
 * The switch before each day is not a stored fact: the wire knows only windows. Switching a day
 * off PARKS its windows, so switching it back on in the same edit returns them instead of a
 * default; a day with nothing parked comes back with the working morning mobile seeds (8:00–12:00).
 *
 * Everything is minutes since the day's midnight on the trainer's clock — a weekly window is a wall
 * reading on a weekday and nothing more (R3). A window may not reach midnight: the wire's `TimeOnly`
 * has no 24:00, and an end at 00:00 reads as crossing it, so the last minute a window may end on is
 * 23:59.
 *
 * Pure: no React, no DOM, no query hook, no i18next.
 */

import type {
  TrainWithMeModulesBookingDtosRequestsPutWeeklyWindowsRequest as PutWeeklyWindowsRequest,
  TrainWithMeModulesBookingDtosResponsesWeeklyWindowResponse as WeeklyWindowResponse,
} from '~/shared/api/generated/model';

import { minutesOfTime, paddedClock } from './trainerClock';

/** ISO weekdays, Monday first — the order the card lists them in. */
export const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7] as const;

export type Weekday = (typeof WEEKDAYS)[number];

/** `AvailabilityProcessor.MaxWeeklyWindows` — R3's seventy: ten a day, seven days. */
export const MAX_WEEKLY_WINDOWS = 70;

/** The last minute a window may end on: `TimeOnly` has no 24:00, and 00:00 would cross midnight. */
export const LAST_MINUTE = 23 * 60 + 59;

/** One window of the draft. `id` survives every edit, because a refusal is pinned to a row. */
export interface PatternWindow {
  readonly id: string;
  readonly start: number;
  readonly end: number;
}

export interface PatternDay {
  readonly weekday: Weekday;
  /** In clock order as read in; in the order they were written while being edited. */
  readonly windows: readonly PatternWindow[];
  /** What switching the day off put aside, handed back when it is switched on again. */
  readonly parked: readonly PatternWindow[];
}

export interface WeekPattern {
  readonly days: readonly PatternDay[];
  /** The next id's number — ids are minted here, never from a clock or a random source. */
  readonly seq: number;
}

/** Why a day's windows cannot be stored, in `WindowGridValidator`'s own words. */
export type WindowProblem =
  | { readonly code: 'window_empty' }
  | { readonly code: 'window_crosses_midnight' }
  | {
      readonly code: 'window_overlap';
      /** Where the two overlap, and the one window they would make together. */
      readonly from: number;
      readonly to: number;
      readonly mergedStart: number;
      readonly mergedEnd: number;
    };

export interface DayVerdict {
  /** The windows to mark. An overlap marks BOTH of the windows that collide. */
  readonly bad: readonly string[];
  readonly problem: WindowProblem | null;
}

export interface PatternVerdict {
  readonly days: Readonly<Record<Weekday, DayVerdict>>;
  /** The week holds more than {@link MAX_WEEKLY_WINDOWS} — a refusal of the WEEK, not of a row. */
  readonly tooMany: boolean;
  /** Whether „Zapisz" may send. An EMPTY week may: that is how a trainer clears their hours. */
  readonly canSave: boolean;
}

const DEFAULT_START = 8 * 60;
const DEFAULT_LENGTH = 4 * 60;

/**
 * The server's week as a draft. A malformed or reversed window is dropped rather than drawn: an
 * unreadable clock is not an hour anybody can change, and the server would not have stored one.
 */
export function weekPatternOf(weekly: readonly WeeklyWindowResponse[]): WeekPattern {
  let seq = 0;
  const days = WEEKDAYS.map((weekday) => {
    const windows = weekly
      .filter((entry) => entry.dayOfWeek === weekday)
      .flatMap((entry) => {
        const start = minutesOfTime(entry.start);
        const end = minutesOfTime(entry.end);
        if (start === null || end === null || end <= start) return [];
        seq += 1;
        return [{ id: `w${String(seq)}`, start, end }];
      })
      .sort((a, b) => a.start - b.start);
    return { weekday, windows, parked: [] };
  });
  return { days, seq };
}

function withDay(
  pattern: WeekPattern,
  weekday: Weekday,
  change: (day: PatternDay) => PatternDay,
  seq = pattern.seq,
): WeekPattern {
  return {
    seq,
    days: pattern.days.map((day) => (day.weekday === weekday ? change(day) : day)),
  };
}

function dayOf(pattern: WeekPattern, weekday: Weekday): PatternDay {
  return (
    pattern.days.find((day) => day.weekday === weekday) ?? { weekday, windows: [], parked: [] }
  );
}

/**
 * Where a new window on a day would go: after its last one, or the working morning on an empty
 * day, kept inside the day. `null` when the day has no minute left before 23:59 — seeding one
 * anyway would hand the trainer a `window_empty` they never asked for.
 */
export function nextWindowSeed(
  windows: readonly { readonly end: number }[],
): { readonly start: number; readonly end: number } | null {
  const lastEnd = windows.reduce((latest, window) => Math.max(latest, window.end), 0);
  const start = lastEnd === 0 ? DEFAULT_START : lastEnd;
  if (start >= LAST_MINUTE) return null;
  return { start, end: Math.min(start + DEFAULT_LENGTH, LAST_MINUTE) };
}

/** Switch a day on (its parked windows back, or the working morning) or off (windows parked). */
export function dayToggled(pattern: WeekPattern, weekday: Weekday): WeekPattern {
  const day = dayOf(pattern, weekday);
  if (day.windows.length > 0) {
    return withDay(pattern, weekday, () => ({ weekday, windows: [], parked: day.windows }));
  }
  if (day.parked.length > 0) {
    return withDay(pattern, weekday, () => ({ weekday, windows: day.parked, parked: [] }));
  }
  return windowAdded(pattern, weekday);
}

/** A new window on a day, seeded with real hours. Unchanged when the day has no room left. */
export function windowAdded(pattern: WeekPattern, weekday: Weekday): WeekPattern {
  const day = dayOf(pattern, weekday);
  const seed = nextWindowSeed(day.windows);
  if (seed === null) return pattern;
  const seq = pattern.seq + 1;
  return withDay(
    pattern,
    weekday,
    () => ({ weekday, windows: [...day.windows, { id: `w${String(seq)}`, ...seed }], parked: [] }),
    seq,
  );
}

/** One window retimed; every other window, and every id, untouched. */
export function windowRetimed(
  pattern: WeekPattern,
  weekday: Weekday,
  id: string,
  change: { readonly start?: number; readonly end?: number },
): WeekPattern {
  return withDay(pattern, weekday, (day) => ({
    ...day,
    windows: day.windows.map((window) => (window.id === id ? { ...window, ...change } : window)),
  }));
}

export function windowRemoved(pattern: WeekPattern, weekday: Weekday, id: string): WeekPattern {
  return withDay(pattern, weekday, (day) => ({
    ...day,
    windows: day.windows.filter((window) => window.id !== id),
  }));
}

/**
 * The source day's windows REPLACE each target day's — mobile's „Podmieni okna w tych dniach".
 * Copying a free day is therefore how several days are cleared at once. Each copy gets its own id.
 */
export function dayCopied(
  pattern: WeekPattern,
  from: Weekday,
  onto: readonly Weekday[],
): WeekPattern {
  const source = dayOf(pattern, from).windows;
  let seq = pattern.seq;
  const days = pattern.days.map((day) => {
    if (day.weekday === from || !onto.includes(day.weekday)) return day;
    const windows = source.map((window) => {
      seq += 1;
      return { id: `w${String(seq)}`, start: window.start, end: window.end };
    });
    return { weekday: day.weekday, windows, parked: [] };
  });
  return { days, seq };
}

/** „Skopiuj poniedziałek na cały tydzień". */
export function mondayCopiedToWeek(pattern: WeekPattern): WeekPattern {
  return dayCopied(pattern, 1, [2, 3, 4, 5, 6, 7]);
}

/**
 * One day judged by `WindowGridValidator`'s two passes, in its order: a window's own shape first (a
 * window covering no time cannot be compared with anything), then neighbours in clock order.
 * Windows that MEET are fine — the server's test is `start >= previous end`.
 */
export function dayVerdict(windows: readonly PatternWindow[]): DayVerdict {
  for (const window of windows) {
    if (window.end > window.start) continue;
    return {
      bad: [window.id],
      // Two mistakes wear one shape; only a reversed window could have meant „past midnight".
      problem: { code: window.end === window.start ? 'window_empty' : 'window_crosses_midnight' },
    };
  }
  const ordered = [...windows].sort((a, b) => a.start - b.start || a.end - b.end);
  for (let index = 1; index < ordered.length; index += 1) {
    const previous = ordered[index - 1];
    const current = ordered[index];
    if (previous === undefined || current === undefined || current.start >= previous.end) continue;
    return {
      bad: [previous.id, current.id],
      problem: {
        code: 'window_overlap',
        from: current.start,
        to: Math.min(previous.end, current.end),
        mergedStart: previous.start,
        mergedEnd: Math.max(previous.end, current.end),
      },
    };
  }
  return { bad: [], problem: null };
}

/** The whole week at once: each day's shape, and the one bound only the week can breach. */
export function patternVerdict(pattern: WeekPattern): PatternVerdict {
  const days = Object.fromEntries(
    WEEKDAYS.map((weekday) => [weekday, dayVerdict(dayOf(pattern, weekday).windows)]),
  ) as Record<Weekday, DayVerdict>;
  const count = pattern.days.reduce((sum, day) => sum + day.windows.length, 0);
  const tooMany = count > MAX_WEEKLY_WINDOWS;
  return {
    days,
    tooMany,
    canSave: !tooMany && WEEKDAYS.every((weekday) => days[weekday].problem === null),
  };
}

/** How many minutes a week of this pattern works — the card's „46 h tygodniowo". */
export function weeklyMinutes(pattern: WeekPattern): number {
  return pattern.days.reduce(
    (sum, day) =>
      sum +
      day.windows.reduce((daySum, window) => daySum + Math.max(0, window.end - window.start), 0),
    0,
  );
}

/**
 * `PUT /trainer-availability/weekly`'s body. Ordered by weekday and clock, so one grid always
 * produces one identical request — which is also what {@link patternChanged} compares.
 */
export function weeklyRequest(pattern: WeekPattern): PutWeeklyWindowsRequest {
  return {
    windows: pattern.days.flatMap((day) =>
      [...day.windows]
        .sort((a, b) => a.start - b.start || a.end - b.end)
        .map((window) => ({
          dayOfWeek: day.weekday,
          start: paddedClock(window.start),
          end: paddedClock(window.end),
        })),
    ),
  };
}

/** Whether the draft would send anything other than what the server holds. */
export function patternChanged(
  pattern: WeekPattern,
  weekly: readonly WeeklyWindowResponse[],
): boolean {
  return (
    JSON.stringify(weeklyRequest(pattern)) !== JSON.stringify(weeklyRequest(weekPatternOf(weekly)))
  );
}
